import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map, take, takeUntil } from 'rxjs/operators';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { HasSubscription } from 'src/ca-shared/ca-shared.module';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { ConversationRedactionMode } from 'src/core/models/generic-lookup-type/conversation/redaction-mode.glt';
import { QueryService } from 'src/core/services/query/query.service';
import { PhraseTypeSettingsService } from 'src/core/services/settings/phrase-type-settings.service';
import { PhraseTypeDto } from 'src/core/models/conversation/phrase-type/phrase-type.dto';
import { ConversationRedactionType } from 'src/core/models/generic-lookup-type/conversation/redaction-type.glt';
import { CustomPhraseTypeComponent } from './custom-phrase-type/custom-phrase-type.component';

@Component({
  selector: 'ca-redaction-settings',
  templateUrl: './redaction-settings.component.html',
  styleUrls: ['./redaction-settings.component.scss'],
  standalone: false,
})
export class RedactionSettingsComponent extends HasSubscription implements OnInit {
  @ViewChild('customPhraseTypeSelector', { static: false, read: CustomPhraseTypeComponent })
  customPhraseTypeSelector: CustomPhraseTypeComponent;

  settingsForm: FormGroup;
  redactionSettingDtos: ConfigurationSettingsDto[] = [];
  redactionModes: GenericLookupDto[];
  redactionTypes: GenericLookupDto[];
  defaultRedactionModeCode: string;
  defaultRedactionTypeCode: string;
  staticRedactionTypeCode: string;
  phraseTypeList: PhraseTypeDto[] = [];
  selectedPhraseTypes: number[] = [];
  groupByList: any = [];
  defaultPhraseTypeNames = ['EMail', 'NationalIdNumber', 'PassportNumber', 'SocialSecurityNumber'];
  loading: boolean = false;
  performInternalOperationsWithMaskedDataEnabled: boolean = true;
  performLLMWithMaskedDataEnabled: boolean = true;

  private readonly settingKeyRedactionEnabled = 'Global.DynamicDataMaskingEnabled';
  private readonly settingKeyRedactionMode = 'Redaction.RedactionMode';
  private readonly settingKeyRedactionTemplates = 'PhraseType.RedactionTemplates';
  private readonly settingKeyRedactionType = 'PhraseType.RedactionType';
  private readonly settingKeyPerformInternalOperationsWithMaskedDataEnabled =
    'Redaction.PerformInternalOperationsWithMaskedDataEnabled';
  private readonly settingKeyPerformLLMWithMaskedDataEnabled =
    'Redaction.PerformLLMWithMaskedDataEnabled';
  private readonly settingKeyCustomPhraseTypes = 'PhraseType.CustomPhraseTypes';

  get disableSaveButton(): boolean {
    if (this.settingsForm.get('redactionEnabled').value) {
      return this.selectedPhraseTypes.length <= 0 || this.loading;
    }
    return false;
  }

  constructor(
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private store: Store,
    private config: ConfigStateService,
    private fb: FormBuilder,
    private queryService: QueryService,
    private phraseTypeSettingsService: PhraseTypeSettingsService
  ) {
    super();
    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .pipe(map(filterFn => filterFn(ConversationRedactionType)))
      .subscribe(result => {
        this.redactionTypes = result;
        this.defaultRedactionTypeCode = this.redactionTypes.find(
          f => f.id === ConversationRedactionType.dynamic
        ).code;
        this.staticRedactionTypeCode = this.redactionTypes.find(
          f => f.id === ConversationRedactionType.static
        ).code;
      });
    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .pipe(map(filterFn => filterFn(ConversationRedactionMode)))
      .subscribe(result => {
        this.redactionModes = result;
        this.defaultRedactionModeCode = this.redactionModes.find(
          f => f.id === ConversationRedactionMode.replaceWithEntityType
        ).code;
      });
    let redactionTypeCode =
      this.config.getSetting(this.settingKeyRedactionType) ?? this.defaultRedactionTypeCode;
    let redactionModeCode =
      this.config.getSetting(this.settingKeyRedactionMode) ?? this.defaultRedactionModeCode;
    let redactionEnabled = this.phraseTypeSettingsService.redactionEnabled;
    this.performInternalOperationsWithMaskedDataEnabled =
      this.phraseTypeSettingsService.dynamicRedactionWithPerformInternalOperationsWithMaskedDataEnabled;
    this.performLLMWithMaskedDataEnabled =
      this.phraseTypeSettingsService.dynamicRedactionWithPerformLLMEnabled;

    this.settingsForm = fb.group({
      redactionEnabled: [redactionEnabled],
      redactionMode: [redactionModeCode],
      redactionType: [redactionTypeCode],
      performInternalOperationsWithMaskedDataEnabled: [
        this.performInternalOperationsWithMaskedDataEnabled,
      ],
      performLLMWithMaskedDataEnabled: [this.performLLMWithMaskedDataEnabled],
    });

    if (redactionEnabled) {
      this.setRedactionEntities(false);
    }
  }

  ngOnInit(): void {}

  getSelectedCountText(): string {
    let selectionText = this.localizationService.instant(
      'Settings::NamedEntityRecognitionEntities'
    );
    selectionText = selectionText.replace('{0}', this.selectedPhraseTypes?.length.toString());
    selectionText = selectionText.replace('{1}', this.phraseTypeList.length.toString());
    return selectionText;
  }

  isSelected(entity: PhraseTypeDto) {
    return this.selectedPhraseTypes.includes(entity.id);
  }

  setRedactionEntities(redactionEnabledChanged: boolean) {
    this.loading = true;
    this.phraseTypeSettingsService
      .getRedactionTemplates()
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.selectedPhraseTypes = [];
          this.phraseTypeList = response.phraseTypes;
          const removedPhraseTypeNames = response.removedPhraseTypeNames;
          this.phraseTypeList = this.phraseTypeList.map(p =>
            p.isUsedInRecognition ? { ...p, disabled: true } : p
          );
          if (redactionEnabledChanged) {
            let defaultPhraseTypeIds = this.phraseTypeList
              .filter(p => this.defaultPhraseTypeNames.includes(p.name) && !p.isUsedInRecognition)
              .map(t => t.id);
            const combinedSelectedPhraseTypeIds = [
              ...this.selectedPhraseTypes,
              ...defaultPhraseTypeIds,
            ];
            this.selectedPhraseTypes = combinedSelectedPhraseTypeIds.filter(
              (item, index) => combinedSelectedPhraseTypeIds.indexOf(item) === index
            );
          } else {
            const enabledPhraseTypeIds = this.phraseTypeList
              .filter(x => x.isEnabled)
              .map(t => t.id);
            this.selectedPhraseTypes = enabledPhraseTypeIds ?? [];
          }
          if (removedPhraseTypeNames.length > 0) {
            this.toastr.warn(
              this.localizationService.instant(
                'Settings::RemovedPhraseTypes',
                removedPhraseTypeNames.join(',')
              )
            );
          }
          if (this.phraseTypeList.length == 0) {
            this.toastr.info(this.localizationService.instant('Settings::NoRedactionEntity'));
          }

          this.groupByList = this.groupByCategory(this.phraseTypeList);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  onEntityChange(event, phraseType: PhraseTypeDto) {
    if (event.target.checked) {
      this.selectedPhraseTypes.push(phraseType.id);
    } else {
      this.selectedPhraseTypes = this.selectedPhraseTypes.filter(id => id != phraseType.id);
    }
  }

  groupByCategory(phraseTypeList: any[]): any {
    return phraseTypeList.reduce((grouped, phraseType) => {
      const category = phraseType.category || 'Others';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(phraseType);
      return grouped;
    }, {});
  }

  onRedactionEnabledChanged(eventArgs) {
    if (eventArgs.target.checked) {
      this.queryService.getExistCallerOrCalledNumber().subscribe(response => {
        if (response === true) {
          this.settingsForm.get('redactionEnabled').setValue(false);
          this.settingsForm.get('redactionType').setValue(this.defaultRedactionTypeCode);
          this.settingsForm.get('redactionType').clearValidators();
          this.settingsForm.get('redactionMode').setValue(this.defaultRedactionModeCode);
          this.settingsForm.get('redactionMode').clearValidators();
          this.performInternalOperationsWithMaskedDataEnabled = false;
          this.performLLMWithMaskedDataEnabled = false;
          this.settingsForm.get('performInternalOperationsWithMaskedDataEnabled').setValue(false);
          this.settingsForm.get('performLLMWithMaskedDataEnabled').setValue(false);
          this.settingsForm.get('performLLMWithMaskedDataEnabled').clearValidators();
          return;
        }

        this.setRedactionEntities(true);
        this.settingsForm.get('redactionType').setValidators(Validators.required);
        this.settingsForm.get('redactionType').setValue(this.defaultRedactionTypeCode);
        this.settingsForm.get('redactionType').updateValueAndValidity();
        this.settingsForm.get('redactionMode').setValidators(Validators.required);
        this.settingsForm.get('redactionMode').setValue(this.defaultRedactionModeCode);
        this.settingsForm.get('redactionMode').updateValueAndValidity();
        this.performInternalOperationsWithMaskedDataEnabled = true;
        this.performLLMWithMaskedDataEnabled = true;
        this.settingsForm.get('performLLMWithMaskedDataEnabled').setValidators(Validators.required);
        this.settingsForm.get('performInternalOperationsWithMaskedDataEnabled').setValue(true);
        this.settingsForm.get('performLLMWithMaskedDataEnabled').setValue(true);
        this.settingsForm.get('performLLMWithMaskedDataEnabled').updateValueAndValidity();
      });
    } else {
      this.phraseTypeList = [];
      this.selectedPhraseTypes = [];
      this.settingsForm.get('redactionType').setValue(null);
      this.settingsForm.get('redactionType').clearValidators();
      this.settingsForm.get('redactionMode').setValue(this.defaultRedactionModeCode);
      this.settingsForm.get('redactionMode').clearValidators();
      this.performInternalOperationsWithMaskedDataEnabled = false;
      this.performLLMWithMaskedDataEnabled = false;
      this.settingsForm.get('performInternalOperationsWithMaskedDataEnabled').setValue(false);
      this.settingsForm.get('performLLMWithMaskedDataEnabled').setValue(false);
      this.settingsForm.get('performLLMWithMaskedDataEnabled').clearValidators();
    }
  }

  onRedactionTypeChange(eventArgs) {
    if (this.settingsForm.get('redactionType').value == this.staticRedactionTypeCode) {
      this.performInternalOperationsWithMaskedDataEnabled = false;
      this.performLLMWithMaskedDataEnabled = false;
      this.settingsForm.get('performInternalOperationsWithMaskedDataEnabled').setValue(false);
      this.settingsForm.get('performLLMWithMaskedDataEnabled').setValue(false);
    } else if (this.settingsForm.get('redactionType').value == this.defaultRedactionTypeCode) {
      this.performInternalOperationsWithMaskedDataEnabled = true;
      this.performLLMWithMaskedDataEnabled = true;
      this.settingsForm.get('performInternalOperationsWithMaskedDataEnabled').setValue(true);
      this.settingsForm.get('performLLMWithMaskedDataEnabled').setValue(true);
    }
  }

  onDynamicRedactionModeChange(eventArgs) {
    if (eventArgs.target.checked) {
      this.settingsForm.get('redactionType').setValue(this.defaultRedactionTypeCode);
    }
  }

  onSubmitSettings() {
    if (this.settingsForm.invalid) {
      return;
    }
    this.saveSettings();
  }

  saveSettings() {
    if (this.settingsForm.invalid) {
      return;
    }

    this.addRedactionSettings();

    this.settingService
      .saveSetting(this.redactionSettingDtos)
      .pipe(take(1))
      .subscribe(res => {
        this.toastr.success(
          this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
        );

        let redactionEnabled = this.settingsForm.get('redactionEnabled').value;
        if (redactionEnabled) {
          let originalLanguages = this.config
            .getSetting('Translation.OriginalLanguageCode')
            .split(';')
            .filter(x => x.length > 0);
          this.phraseTypeSettingsService.getLanguages(true).subscribe(languagesDataSource => {
            languagesDataSource.sort((x, y) => (x.name > y.name ? 1 : -1));

            const filteredOriginalLanguages = originalLanguages.filter(code =>
              languagesDataSource.some(lang => lang.code === code)
            );

            const translationSettingDtos = [
              {
                settingName: 'Translation.OriginalLanguageCode',
                settingValue: filteredOriginalLanguages.join(';'),
              },
            ];

            this.settingService
              .saveSetting(translationSettingDtos)
              .pipe(take(1))
              .subscribe(res => {});
          });
        }
      });
  }

  private addRedactionSettings() {
    this.redactionSettingDtos = new Array();
    let redactionEnabled = this.settingsForm.get('redactionEnabled').value;

    this.redactionSettingDtos.push({
      settingName: this.settingKeyRedactionEnabled,
      settingValue: redactionEnabled.toString(),
    });

    this.redactionSettingDtos.push({
      settingName: this.settingKeyRedactionType,
      settingValue: redactionEnabled
        ? this.settingsForm.get('redactionType').value.toString()
        : this.defaultRedactionTypeCode,
    });

    this.redactionSettingDtos.push({
      settingName: this.settingKeyRedactionMode,
      settingValue: redactionEnabled
        ? this.settingsForm.get('redactionMode').value.toString()
        : this.defaultRedactionModeCode,
    });

    this.redactionSettingDtos.push({
      settingName: this.settingKeyRedactionTemplates,
      settingValue: JSON.stringify(redactionEnabled ? this.selectedPhraseTypes ?? [] : []),
    });

    this.redactionSettingDtos.push({
      settingName: this.settingKeyPerformInternalOperationsWithMaskedDataEnabled,
      settingValue: redactionEnabled
        ? this.settingsForm
            .get('performInternalOperationsWithMaskedDataEnabled')
            .value.toString() ?? false
        : false,
    });

    this.redactionSettingDtos.push({
      settingName: this.settingKeyPerformLLMWithMaskedDataEnabled,
      settingValue: redactionEnabled
        ? this.settingsForm.get('performLLMWithMaskedDataEnabled').value.toString() ?? false
        : false,
    });

    this.redactionSettingDtos.push({
      settingName: this.settingKeyCustomPhraseTypes,
      settingValue: JSON.stringify(
        redactionEnabled ? this.customPhraseTypeSelector?.selectedCustomPhraseTypeIds ?? [] : []
      ),
    });

    if (this.customPhraseTypeSelector) {
      this.customPhraseTypeSelector.initiallySelectedCustomPhraseTypeIds = [
        ...(this.customPhraseTypeSelector?.selectedCustomPhraseTypeIds ?? []),
      ];
    }
  }
}
